import { useFocusEffect } from "@react-navigation/native";
import { getDeliveryOrderItems } from "@services/api";
import React, { useCallback, useMemo, useState } from "react";
import {
  Modal,
  Pressable,
  SectionList,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import Text from "@components/CustomText";
import Cross from "@assets/svg/CrossIcon";
import { colors } from "@constants/theme";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import OrderItemCard from "@screens/CustomerOrderItems/components/CrateCard/OrderItemCard";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import BottomButton from "./BottomButton";
import ReturnReasonDropdown from "./ReturnReasonDropdown";
import t from "@constants/localization";
interface MarkingDeliveryModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  item: any;
  key: any;
  onMarkingOrder: (orderId: string, value: any) => void;
  closemarkingModal: () => void;
}

const MarkingDeliveryModalContent: React.FC<
  Omit<MarkingDeliveryModalProps, "showModal" | "key"> & { showModal: boolean }
> = ({ showModal, setShowModal, item, onMarkingOrder, closemarkingModal }) => {
  const PATH = "screens/MyTask/MarkingModal";
  // Read insets from the SafeAreaProvider nested INSIDE the Modal (below), so
  // the value reflects the modal's own translucent window (which draws behind
  // the nav bar). Reading from the root provider returns bottom: 0 while a
  // navigationBarTranslucent modal is open, leaving the submit button clipped
  // behind the soft-nav bar.
  const insets = useSafeAreaInsets();
  const [isLoading, setIsLoading] = useState(false);
  const [ordersItem, setOrdersItem] = useState([]);

  const [isOfdCp, setIsOfdCp] = useState(false);
  const [imagesMandatoryForSamasya, setImagesMandatoryForSamasya] = useState(
    {},
  );
  const [warning, setWarning] = useState({
    show_warning: false,
    title: "",
    message: "",
    is_highly_discounted_cart: false,
  });
  const [value, setValue] = useState(null);
  const [items, setItems] = useState([]);
  const [markingConfig, setMarkingConfig] = useState([]);
  const [allData, setAllData] = useState({});
  useFocusEffect(
    useCallback(() => {
      if (showModal) {
        fetchData();
      }
    }, [showModal]),
  );

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getDeliveryOrderItems({
        order_ids: item?.order_ids,
        spoke_name: item?.spokeName || "",
        user_id: item?.user_id,
        date: item?.date || "",
        source: "PRE_DELIVERY",
        type: item?.key,
      });
      setAllData(data);
      setMarkingConfig(data?.marking_config || []);
      setOrdersItem(data?.data);
      setIsOfdCp(data?.is_ofd_cp);
      setImagesMandatoryForSamasya(data?.images_mandatory_for_samasya);
      setWarning(data?.marking_warning || null);
      setItems(data?.cx_rto_reasons);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  const checkItemDatailsEmpty = () => {
    return !sortUpdates.find((v) => v?.data?.length > 0);
  };
  const sortUpdates = useMemo(() => {
    return ordersItem.map((v) => {
      return {
        ...v,
        data: v.items,
      };
    });
  }, [ordersItem]);
  const keyExtractor = (item, index) => `${item.id}_${index}`;
  const renderItem = useCallback(
    ({ item, section }) => {
      return (
        <OrderItemCard
          info={item}
          markingConfig={markingConfig}
          fetchData={fetchData}
          imagesMandatoryForSamasya={imagesMandatoryForSamasya}
          source="PRE_DELIVERY"
          isOfdCp={isOfdCp}
          setShowModal={setShowModal}
          isHighlyDiscountedItem={warning?.is_highly_discounted_cart}
        />
      );
    },
    [imagesMandatoryForSamasya],
  );

  const footerComponent = () => <View style={{ height: 400 }} />;

  const disableRtoWithoutReason = () =>
    item?.key === "CANCEL_DELIVERY" && !value;

  return (
    <>
      <TouchableWithoutFeedback onPress={() => closemarkingModal()}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={styles.contentContainer}>
        <View style={styles.modalCross}>
          <Pressable onPress={() => closemarkingModal()}>
            <Cross width={16} height={16} />
          </Pressable>
        </View>
        <View style={styles.headingContainer}>
          <Text variant="title_20">
            {item?.key === "CANCEL_DELIVERY"
              ? t(`${PATH}/cancel_header`)
              : t(`${PATH}/delivery_header`)}
          </Text>
          {item?.key !== "CANCEL_DELIVERY" ? (
            <View style={styles.quantityContainer}>
              <Text variant="caption_1">{t(`${PATH}/total_quantity`)}</Text>
              <View style={styles.quantity}>
                <Text variant="title_20">{allData?.order_items_count}</Text>
              </View>
            </View>
          ) : null}
        </View>
        {isLoading ? (
          <View style={{ padding: 16 }}>
            <SkeletonLoaderList isLoading={isLoading} length={8} />
          </View>
        ) : checkItemDatailsEmpty() ? (
          <EmptySearchList />
        ) : (
          <View>
            {item?.key === "CANCEL_DELIVERY" ? (
              <ReturnReasonDropdown
                value={value}
                setValue={setValue}
                items={items}
                setItems={setItems}
              />
            ) : null}
            <SectionList
              keyExtractor={keyExtractor}
              sections={sortUpdates}
              renderItem={renderItem}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              ListFooterComponent={footerComponent}
            />
          </View>
        )}
        <BottomButton
          onPress={() => {
            onMarkingOrder(item?.order_ids[0], {
              key: item?.key,
              reason: value,
              order_id: item?.order_ids[0],
            });
            setValue(null);
            closemarkingModal();
          }}
          title={t(`${PATH}/submit`)}
          disable={disableRtoWithoutReason() || isLoading}
          bottomInset={insets.bottom}
        />
      </View>
    </>
  );
};

const MarkingDeliveryModal: React.FC<MarkingDeliveryModalProps> = (props) => {
  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={!!props.showModal}
      onRequestClose={() => props.closemarkingModal()}
      statusBarTranslucent
      navigationBarTranslucent
    >
      {/* Nested provider measures the modal's own translucent window so
          useSafeAreaInsets() reports the real nav-bar inset inside the Modal. */}
      <SafeAreaProvider>
        <MarkingDeliveryModalContent {...props} />
      </SafeAreaProvider>
    </Modal>
  );
};

export default MarkingDeliveryModal;

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  contentContainer: {
    backgroundColor: "#EDEFF6",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
    minHeight: "55%",
    maxHeight: "85%",
  },

  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    paddingTop: 20,
    alignItems: "center",
  },

  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: "#fff",
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },

  quantityContainer: {
    flexDirection: "row",
    alignItems: "center",
  },

  quantity: {
    borderRadius: 8,
    marginLeft: 4,
    padding: 8,
    paddingVertical: 4,
    borderColor: colors.mediumBlack,
    borderWidth: 0.5,
  },
});
